import React, { useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import {
  useCreateDefaultBusinessSettings,
  useUpdateDefaultBusinessSettings,
  useUpsertBusinessSettings,
} from '~/hooks/apis/business-traveller/useBusinessSettings';
import useToggleState from '~/hooks/useToggleState';

import { BusinessSettings, Societe, SocieteTier } from '~/services/BusinessTraveller/BusinessTravellerService';

import {
  SOCIETE_TIERS,
  SOCIETE_TIER_LABELS,
  SUPPORTED_SOCIETE_TIERS,
  SocieteForm,
  SocieteFormValue,
  formToSociete,
  societeToForm,
} from '~/utils/businessTraveller/businessSettings';

import DeleteBusinessSettingDialog from './DeleteBusinessSettingDialog';

// Form's view of a societe setting — id is absent on a fresh draft (before first save).
export interface SocieteSettingDraft {
  id?: string;
  value: Societe;
}

interface Props {
  setting: SocieteSettingDraft;
  // Undefined when this form belongs to the global defaults scope.
  businessId?: string;
}

export default function SocieteSettingForm({ setting, businessId }: Props) {
  const upsertMutation = useUpsertBusinessSettings();
  const createDefaultMutation = useCreateDefaultBusinessSettings();
  const updateDefaultMutation = useUpdateDefaultBusinessSettings();

  const isNew = !setting.id;

  const [societeState, setSocieteState] = useState<SocieteForm>(() => societeToForm(setting.value));

  const updateTier = (tier: SocieteTier, patch: Partial<SocieteFormValue>) => {
    setSocieteState((prev) => ({ ...prev, [tier]: { ...prev[tier], ...patch } }));
  };

  const isTierValid = (tier: SocieteTier) => {
    const { threshold } = societeState[tier];
    return threshold !== '' && Number(threshold) >= 1;
  };

  const hasInvalidTier = SOCIETE_TIERS.some((tier) => !isTierValid(tier));
  const isPending = upsertMutation.isPending || createDefaultMutation.isPending || updateDefaultMutation.isPending;

  // Delete is only meaningful for a persisted business-specific setting; defaults can't be deleted.
  const canDelete = !!businessId && !!setting.id;
  const { isToggled: isDeleteDialogOpen, toggleOn: openDeleteDialog, toggleOff: closeDeleteDialog } = useToggleState();

  const handleSave = () => {
    const settings: BusinessSettings = { societe: formToSociete(societeState) };

    if (businessId) {
      upsertMutation.mutate({ businessId, settings });
    } else if (setting.id) {
      updateDefaultMutation.mutate({ id: setting.id, settings });
    } else {
      createDefaultMutation.mutate(settings);
    }
  };

  return (
    <Card variant="outlined" sx={{ width: '100%' }}>
      <CardContent>
        <Stack gap={2}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Societe Config</Typography>
            {canDelete && (
              <IconButton onClick={openDeleteDialog} aria-label="delete-setting" disabled={isPending}>
                <DeleteIcon />
              </IconButton>
            )}
          </Stack>

          {SOCIETE_TIERS.map((tier) => {
            const tierLabel = SOCIETE_TIER_LABELS[tier];
            const isValid = isTierValid(tier);
            return (
              <Stack key={tier} direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                <Stack direction="row" alignItems="center" gap={2}>
                  <TextField
                    label={`${tierLabel} threshold`}
                    helperText={isValid ? `Threshold for Societe ${tierLabel}` : 'Enter a number greater than 0'}
                    type="number"
                    value={societeState[tier].threshold}
                    onChange={(event) => updateTier(tier, { threshold: event.target.value })}
                    error={!isValid}
                    slotProps={{ htmlInput: { min: 1, step: 1 } }}
                    sx={{ maxWidth: 240 }}
                    required
                  />
                  {!SUPPORTED_SOCIETE_TIERS.includes(tier) && (
                    <Typography variant="body2" color="error" sx={{ fontStyle: 'italic' }}>
                      Assignment of this Societe tier ({tierLabel}) is not supported - shown for illustrative purposes
                      only
                    </Typography>
                  )}
                </Stack>

                {/* Disable switch */}
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Switch
                      checked={societeState[tier].enabled}
                      onChange={(event) => updateTier(tier, { enabled: event.target.checked })}
                      inputProps={{ 'aria-label': `societe-${tier}-enabled` }}
                    />
                  }
                  label={`${tierLabel} enabled`}
                />
              </Stack>
            );
          })}

          <Box>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={isPending || hasInvalidTier}>
              {isNew ? 'Create' : 'Save'}
            </Button>
          </Box>
        </Stack>
      </CardContent>

      {canDelete && businessId && setting.id && (
        <DeleteBusinessSettingDialog
          businessId={businessId}
          settingId={setting.id}
          settingName="societe"
          isOpen={isDeleteDialogOpen}
          onClose={closeDeleteDialog}
        />
      )}
    </Card>
  );
}
